import type {
  EmbedClientContext,
  WebClientSpanName,
  WebGuestFrameLifecycleEvent,
  WebGuestFramePath,
  WebGuestFrameResourceEvent,
  WebGuestQueryEvent,
} from '@doss/common-all'
import {
  GUEST_FRAME_PATH,
  type FrameMemorySource,
  type FrameResourceSnapshotMessage,
} from '@doss/guest-frame'
import { newClientTraceContext, newSpanId, type ClientTraceContext } from './client-spans'

// Observation vocabulary derives from the shared wire contract
// (@doss/common-all web-telemetry-events.ts) so the values can never drift
// from what the telemetry route accepts.
export type GuestFrameLifecycleOperation = WebGuestFrameLifecycleEvent['operation']

export type GuestFrameLifecycleStatus = WebGuestFrameLifecycleEvent['status']

export type GuestFrameErrorCode = NonNullable<WebGuestFrameLifecycleEvent['error_code']>

export type GuestFrameLifecycleObservation = {
  type: 'lifecycle'
  operation: GuestFrameLifecycleOperation
  status: GuestFrameLifecycleStatus
  errorCode?: GuestFrameErrorCode
  durationMs: number
  frameCount: number
  visibleFrameCount: number
  runtimeBuildSha?: string
  /** Which execution path hosted the frame, so rollout compares the two. */
  path?: WebGuestFramePath
}

export type GuestFrameResourceStatus = WebGuestFrameResourceEvent['status']

export type GuestFrameResourceObservation = {
  type: 'resource'
  status: GuestFrameResourceStatus
  frameCount: number
  visibleFrameCount: number
  respondingFrameCount: number
  domNodes: number
  longTaskCount: number
  longTaskDurationMs: number
  maxLongTaskDurationMs: number
  sampleWindowMs: number
  memorySource: FrameMemorySource
  memoryUsedBytes?: number
  memoryTotalBytes?: number
  transferBytes: number
  decodedBytes: number
  cacheHitCount: number
}

export type GuestQueryErrorCode = NonNullable<WebGuestQueryEvent['error_code']>

export type GuestQueryObservation = {
  type: 'query'
  status: WebGuestQueryEvent['status']
  source: WebGuestQueryEvent['source']
  errorCode?: GuestQueryErrorCode
  durationMs: number
  /** Anonymous frame-local identity shared by every page of one statement. */
  statementId?: string
  /** Zero-based request position within that statement identity. */
  statementRequestIndex?: number
  /** Backend trace id for this fetch (backend-sourced results only), for trace/log joins. */
  traceId?: string
}

/**
 * A finished browser span belonging to a frame trace (or a standalone fetch).
 * Timestamps are epoch milliseconds; the sink maps this 1:1 onto a
 * client_span beacon event.
 */
export type GuestFrameSpanObservation = {
  type: 'span'
  name: WebClientSpanName
  traceId: string
  spanId: string
  parentSpanId?: string
  startMs: number
  endMs: number
  status: 'ok' | 'error' | 'timeout'
  errorCode?: string
  embedId?: string
  viewContentId?: string
  /** Set on trace-terminal spans: the sink should flush the beacon promptly so
   * spans reach the collector inside the tail sampler's decision window. */
  flush?: boolean
}

export type GuestFrameObservation =
  | GuestFrameLifecycleObservation
  | GuestFrameResourceObservation
  | GuestQueryObservation
  | GuestFrameSpanObservation

export type GuestFrameObservationSink = (observation: GuestFrameObservation) => void

// Dashboard attribution attached to a frame's spans: the same client-asserted
// embed context the backend parses from headers (EmbedClientContext).

type GuestFrameEntry = {
  element: HTMLIFrameElement | null
  mountedAt: number
  visible: boolean
  requestResourceSnapshot?: (includeMemory: boolean) => Promise<FrameResourceSnapshotMessage>
  runtimeBuildSha?: string
  bootReported: boolean
  renderReported: boolean
  queriesReported: boolean
  compileFailureReported: boolean
  runtimeFailureReported: boolean
  /** The frame-load trace every initial-load span parents under. */
  trace: ClientTraceContext
  /** Epoch-clock twin of mountedAt, for span timestamps. */
  mountedAtEpochMs: number
  /** End of the last emitted phase span; the next phase starts here. */
  phaseEndEpochMs: number
  /** True once the guest_frame.load root span was emitted (settle or failure). */
  loadSpanClosed: boolean
  telemetry?: EmbedClientContext
  path?: WebGuestFramePath
}

export type GuestFrameMonitorRegistration = {
  ready: (runtimeBuildSha: string) => void
  rendered: () => void
  queriesSettled: () => void
  failed: (errorCode: GuestFrameErrorCode) => void
  setResourceRequester: (
    requester: (includeMemory: boolean) => Promise<FrameResourceSnapshotMessage>,
  ) => void
  /**
   * The open frame-load trace, for parenting per-query fetch spans; undefined
   * once the load settled/failed (runtime queries mint their own traces).
   */
  traceContext: () => ClientTraceContext | undefined
  unregister: () => void
}

const entries = new Map<number, GuestFrameEntry>()
const entryIdByElement = new WeakMap<HTMLIFrameElement, number>()
let nextEntryId = 1
let observationSink: GuestFrameObservationSink | undefined
let visibilityObserver: IntersectionObserver | undefined
let resourceSampleTimer: number | undefined
let visibilityFlushListener: (() => void) | undefined

const INITIAL_RESOURCE_SAMPLE_DELAY_MS = 30_000
const MEAN_RESOURCE_SAMPLE_INTERVAL_MS = 5 * 60_000

const counts = (): { frameCount: number; visibleFrameCount: number } => {
  let frameCount = 0
  let visibleFrameCount = 0
  for (const entry of entries.values()) {
    if (entry.element !== null) {
      frameCount += 1
      if (entry.visible) {
        visibleFrameCount += 1
      }
    }
  }
  return { frameCount, visibleFrameCount }
}

const emit = (
  entry: GuestFrameEntry,
  observation: Omit<
    GuestFrameLifecycleObservation,
    'type' | 'durationMs' | 'frameCount' | 'visibleFrameCount' | 'runtimeBuildSha'
  >,
): void => {
  observationSink?.({
    type: 'lifecycle',
    ...observation,
    durationMs: Math.max(0, performance.now() - entry.mountedAt),
    ...counts(),
    ...(entry.runtimeBuildSha === undefined ? {} : { runtimeBuildSha: entry.runtimeBuildSha }),
    ...(entry.path === undefined ? {} : { path: entry.path }),
  })
}

export const recordGuestQueryObservation = (
  observation: Omit<GuestQueryObservation, 'type'>,
): void => {
  observationSink?.({ type: 'query', ...observation })
}

/** Emits a finished fetch span (GuestFrame's per-query client spans). */
export const recordGuestSpanObservation = (
  observation: Omit<GuestFrameSpanObservation, 'type'>,
): void => {
  observationSink?.({ type: 'span', ...observation })
}

/**
 * Emits one frame-lifecycle phase span. Phases tile the load: each span starts
 * where the previous one ended, so the trace reads mount -> boot ->
 * first_render -> initial_queries with no invented overlap.
 */
const emitPhaseSpan = (
  entry: GuestFrameEntry,
  name: WebClientSpanName,
  status: 'ok' | 'error' | 'timeout',
  errorCode?: string,
): void => {
  const endMs = Date.now()
  observationSink?.({
    type: 'span',
    name,
    traceId: entry.trace.traceId,
    spanId: newSpanId(),
    parentSpanId: entry.trace.rootSpanId,
    startMs: Math.min(entry.phaseEndEpochMs, endMs),
    endMs,
    status,
    ...(errorCode === undefined ? {} : { errorCode }),
    ...entry.telemetry,
  })
  entry.phaseEndEpochMs = endMs
}

/**
 * Closes the frame-load trace: emits the guest_frame.load root span exactly
 * once (settle or first failure) and asks the sink to flush so the spans reach
 * the collector inside the tail sampler's decision window.
 */
const closeLoadSpan = (
  entry: GuestFrameEntry,
  status: 'ok' | 'error' | 'timeout',
  errorCode?: string,
): void => {
  if (entry.loadSpanClosed) {
    return
  }
  entry.loadSpanClosed = true
  observationSink?.({
    type: 'span',
    name: 'guest_frame.load',
    traceId: entry.trace.traceId,
    spanId: entry.trace.rootSpanId,
    startMs: entry.mountedAtEpochMs,
    endMs: Date.now(),
    status,
    ...(errorCode === undefined ? {} : { errorCode }),
    ...entry.telemetry,
    flush: true,
  })
}

const getVisibilityObserver = (): IntersectionObserver | undefined => {
  if (typeof IntersectionObserver === 'undefined') {
    return undefined
  }
  visibilityObserver ??= new IntersectionObserver((observations) => {
    for (const observation of observations) {
      if (!(observation.target instanceof HTMLIFrameElement)) {
        continue
      }
      const entryId = entryIdByElement.get(observation.target)
      if (entryId === undefined) {
        continue
      }
      const entry = entries.get(entryId)
      if (entry !== undefined) {
        entry.visible = observation.isIntersecting
      }
    }
  })
  return visibilityObserver
}

const reportFailure = (entry: GuestFrameEntry, errorCode: GuestFrameErrorCode): void => {
  if (errorCode === 'compile_error') {
    if (!entry.compileFailureReported) {
      entry.compileFailureReported = true
      emit(entry, { operation: 'compile', status: 'failed', errorCode })
      closeLoadSpan(entry, 'error', errorCode)
    }
    return
  }

  if (errorCode === 'boot_timeout' || errorCode === 'protocol_mismatch') {
    if (!entry.bootReported) {
      entry.bootReported = true
      emit(entry, {
        operation: 'boot',
        status: errorCode === 'boot_timeout' ? 'timeout' : 'failed',
        errorCode,
      })
      closeLoadSpan(entry, errorCode === 'boot_timeout' ? 'timeout' : 'error', errorCode)
    }
    return
  }

  if (!entry.renderReported) {
    entry.renderReported = true
    emit(entry, { operation: 'first_render', status: 'failed', errorCode })
    closeLoadSpan(entry, 'error', errorCode)
    return
  }

  if (!entry.runtimeFailureReported) {
    entry.runtimeFailureReported = true
    emit(entry, { operation: 'runtime', status: 'failed', errorCode })
  }
}

// LEGACY_CJS_MIGRATE: ENG-601 — Account for ESM assets instead of one guest-frame.html resource.
// Preserve runtime resource snapshots and monitoring; only this transfer accounting is legacy.
const resourceTimingTotals = (): {
  transferBytes: number
  decodedBytes: number
  cacheHitCount: number
} => {
  const entries = performance
    .getEntriesByType('resource')
    .filter((entry): entry is PerformanceResourceTiming => {
      if (entry.entryType !== 'resource') {
        return false
      }
      try {
        return new URL(entry.name).pathname === GUEST_FRAME_PATH
      } catch {
        return false
      }
    })
  return {
    transferBytes: entries.reduce((sum, entry) => sum + entry.transferSize, 0),
    decodedBytes: entries.reduce((sum, entry) => sum + entry.decodedBodySize, 0),
    cacheHitCount: entries.filter((entry) => entry.transferSize === 0).length,
  }
}

export const collectGuestFrameResourceObservation =
  async (): Promise<GuestFrameResourceObservation> => {
    const frameEntries = [...entries.values()].filter((entry) => entry.element !== null)
    const requesters = frameEntries.flatMap((entry) =>
      entry.requestResourceSnapshot === undefined ? [] : [entry.requestResourceSnapshot],
    )
    const results = await Promise.allSettled(
      requesters.map((request, index) => request(index === 0)),
    )
    const snapshots = results.flatMap((result) =>
      result.status === 'fulfilled' ? [result.value] : [],
    )
    const memory = snapshots.find((snapshot) => snapshot.memorySource !== 'unsupported')
    const frameCounts = counts()
    let status: GuestFrameResourceStatus = 'partial'
    if (snapshots.length === 0) {
      status = 'failed'
    } else if (snapshots.length === frameEntries.length) {
      status = 'succeeded'
    }

    return {
      type: 'resource',
      status,
      ...frameCounts,
      respondingFrameCount: snapshots.length,
      domNodes: snapshots.reduce((sum, snapshot) => sum + snapshot.domNodes, 0),
      longTaskCount: snapshots.reduce((sum, snapshot) => sum + snapshot.longTaskCount, 0),
      longTaskDurationMs: snapshots.reduce((sum, snapshot) => sum + snapshot.longTaskDurationMs, 0),
      maxLongTaskDurationMs: snapshots.reduce(
        (maximum, snapshot) => Math.max(maximum, snapshot.maxLongTaskDurationMs),
        0,
      ),
      sampleWindowMs: snapshots.reduce(
        (maximum, snapshot) => Math.max(maximum, snapshot.sampleWindowMs),
        0,
      ),
      memorySource: memory?.memorySource ?? 'unsupported',
      ...(memory?.memoryUsedBytes === undefined ? {} : { memoryUsedBytes: memory.memoryUsedBytes }),
      ...(memory?.memoryTotalBytes === undefined
        ? {}
        : { memoryTotalBytes: memory.memoryTotalBytes }),
      ...resourceTimingTotals(),
    }
  }

const randomizedResourceSampleInterval = (): number =>
  -Math.log(Math.max(Number.MIN_VALUE, Math.random())) * MEAN_RESOURCE_SAMPLE_INTERVAL_MS

const scheduleResourceSample = (delayMs: number): void => {
  if (
    resourceSampleTimer !== undefined ||
    observationSink === undefined ||
    counts().frameCount === 0
  ) {
    return
  }
  resourceSampleTimer = window.setTimeout(() => {
    resourceSampleTimer = undefined
    void collectGuestFrameResourceObservation()
      .then((observation) => observationSink?.(observation))
      .finally(() => scheduleResourceSample(randomizedResourceSampleInterval()))
  }, delayMs)
}

const flushResourceSampleWhenHidden = (): void => {
  if (document.visibilityState === 'hidden' && counts().frameCount > 0) {
    void collectGuestFrameResourceObservation().then((observation) =>
      observationSink?.(observation),
    )
  }
}

export const registerGuestFrame = (
  element: HTMLIFrameElement | null,
  telemetry?: EmbedClientContext,
  path?: WebGuestFramePath,
): GuestFrameMonitorRegistration => {
  const entryId = nextEntryId
  nextEntryId += 1
  const mountedAtEpochMs = Date.now()
  const entry: GuestFrameEntry = {
    element,
    mountedAt: performance.now(),
    visible: element !== null,
    bootReported: false,
    renderReported: false,
    queriesReported: false,
    compileFailureReported: false,
    runtimeFailureReported: false,
    requestResourceSnapshot: undefined,
    trace: newClientTraceContext(),
    mountedAtEpochMs,
    phaseEndEpochMs: mountedAtEpochMs,
    loadSpanClosed: false,
    ...(telemetry === undefined ? {} : { telemetry }),
    ...(path === undefined ? {} : { path }),
  }
  entries.set(entryId, entry)
  if (element !== null) {
    entryIdByElement.set(element, entryId)
    getVisibilityObserver()?.observe(element)
  }
  scheduleResourceSample(INITIAL_RESOURCE_SAMPLE_DELAY_MS)

  return {
    ready: (runtimeBuildSha) => {
      if (entry.bootReported) {
        return
      }
      entry.runtimeBuildSha = runtimeBuildSha
      entry.bootReported = true
      emit(entry, { operation: 'boot', status: 'succeeded' })
      emitPhaseSpan(entry, 'guest_frame.boot', 'ok')
    },
    rendered: () => {
      if (entry.renderReported) {
        return
      }
      entry.renderReported = true
      emit(entry, { operation: 'first_render', status: 'succeeded' })
      emitPhaseSpan(entry, 'guest_frame.first_render', 'ok')
    },
    queriesSettled: () => {
      if (entry.queriesReported) {
        return
      }
      entry.queriesReported = true
      emit(entry, { operation: 'initial_queries', status: 'succeeded' })
      emitPhaseSpan(entry, 'guest_frame.initial_queries', 'ok')
      closeLoadSpan(entry, 'ok')
    },
    setResourceRequester: (requester) => {
      entry.requestResourceSnapshot = requester
    },
    failed: (errorCode) => reportFailure(entry, errorCode),
    traceContext: () => (entry.loadSpanClosed ? undefined : entry.trace),
    unregister: () => {
      if (entry.element !== null) {
        visibilityObserver?.unobserve(entry.element)
      }
      entries.delete(entryId)
      if (counts().frameCount === 0 && resourceSampleTimer !== undefined) {
        window.clearTimeout(resourceSampleTimer)
        resourceSampleTimer = undefined
      }
    },
  }
}

export const setGuestFrameObservationSink = (sink: GuestFrameObservationSink): (() => void) => {
  observationSink = sink
  visibilityFlushListener ??= flushResourceSampleWhenHidden
  document.addEventListener('visibilitychange', visibilityFlushListener)
  scheduleResourceSample(INITIAL_RESOURCE_SAMPLE_DELAY_MS)
  return () => {
    if (observationSink === sink) {
      observationSink = undefined
      if (resourceSampleTimer !== undefined) {
        window.clearTimeout(resourceSampleTimer)
        resourceSampleTimer = undefined
      }
      if (visibilityFlushListener !== undefined) {
        document.removeEventListener('visibilitychange', visibilityFlushListener)
        visibilityFlushListener = undefined
      }
    }
  }
}

export const getGuestFrameMonitorSnapshot = (): {
  frameCount: number
  visibleFrameCount: number
} => counts()

export const resetGuestFrameMonitorForTest = (): void => {
  entries.clear()
  visibilityObserver?.disconnect()
  visibilityObserver = undefined
  observationSink = undefined
  if (resourceSampleTimer !== undefined) {
    window.clearTimeout(resourceSampleTimer)
    resourceSampleTimer = undefined
  }
  if (visibilityFlushListener !== undefined) {
    document.removeEventListener('visibilitychange', visibilityFlushListener)
    visibilityFlushListener = undefined
  }
  nextEntryId = 1
}
